import React, { useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import ReactPaginate from "react-paginate";
import AddContacts from "./addContacts";
import { toast } from "react-toastify";
import { Dialog, DialogTitle } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { API_BASE_URL } from "../config/api";

export default function MyContacts({ onTabChange }) {
    const [contact, setContact] = useState([]);
    const [search, setSearch] = useState("");
    const [count, setCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [modalType, setModalType] = useState("");
    const [dataToUpdate, setDataToUpdate] = useState({});
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [selectedContacts, setSelectedContacts] = useState([]);

    const jobId = window.location.search.split("?")[1];

    const handleDelete = (contact) => {
        setContactToDelete(contact);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setContactToDelete(null);
    };

    const handleConfirmDelete = () => {
        handleDeleteOperation(contactToDelete);
        handleCloseDeleteModal();
    };

    const receiveDataFromAddContacts = (data) => {
        setContact((prevContact) => [data, ...prevContact]);
    };

    const handleButtonClick = (id) => {
        onTabChange(id);
    };

    const fetchData = async () => {
        const url = API_BASE_URL;
        const token = localStorage.getItem("authToken");
        setLoading(true);

        const encodedSearch = encodeURIComponent(search);

        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const res = await fetch(
                `${url}/contact_list?search=${encodedSearch}&page_num_start=1&page_size=10`,
                requestOptions
            );
            const body = await res.json();
            setCount(body.data.totalContact / 10);
            setPageCount(body.data.totalContact);
            setContact(body?.data.contact);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaginatedData = async (currentPage) => {
        const url = API_BASE_URL;
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        const res = await fetch(
            `${url}/contact_list?search=${search}&page_num_start=${currentPage}&page_size=10`,
            requestOptions
        );
        const data = await res.json();
        setLoading(false);
        return data;
    };

    const handlePageClick = async (data) => {
        const currentPage = data.selected + 1;
        const dataFromServer = await fetchPaginatedData(currentPage);
        setContact(dataFromServer?.data.contact);
    };

    useEffect(() => {
        fetchData();
    }, [search]);

    const handleDeleteOperation = (contact) => {
        const url = API_BASE_URL;
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        fetch(`${url}/delete_contact/${contact.id}`, requestOptions)
            .then((res) => {
                if (res.ok) return res.json();
                throw new Error("Error deleting contact");
            })
            .then(() => {
                setContact((prevContact) =>
                    prevContact.filter((item) => item.id !== contact.id)
                );
                toast.success("Contact deleted successfully!", { autoClose: 3000 });
            })
            .catch((error) => {
                console.error("Error deleting contact:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleUpdate = (contact) => {
        setModalType("update");
        setDataToUpdate(contact);
    };

    const updateContact = (updatedContact) => {
        setContact((prevContacts) =>
            prevContacts.map((c) => (c.id === updatedContact.id ? updatedContact : c))
        );
    };

    const handleCheckboxChange = (event, id) => {
        const { checked } = event.target;
        if (checked) {
            setSelectedContacts((prev) => [...prev, id]);
        } else {
            setSelectedContacts((prev) => prev.filter((selectedId) => selectedId !== id));
        }
    };

    const handleCheckboxAllChange = (event) => {
        const { checked } = event.target;
        if (checked) {
            setSelectedContacts(contact.map((c) => c.id));
        } else {
            setSelectedContacts([]);
        }
    };

    const handleInvitation = async () => {
        try {
            setLoading(true);
            if (jobId && selectedContacts.length > 0) {
                const url = API_BASE_URL;
                const token = localStorage.getItem("authToken");
                const requestData = new FormData();
                requestData.append("project_id", jobId);
                selectedContacts.forEach((contactId, index) => {
                    requestData.append(`contact_id[${index}]`, contactId);
                });
                const response = await fetch(`${url}/bid_invitation`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: requestData,
                });
                if (response.ok) {
                    const { message } = await response.json();
                    toast.success(message, { autoClose: 3000 });
                }
            } else {
                toast.error("Please select contacts to invite");
            }
        } catch (error) {
            toast.error("An error occurred: " + error, { autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="profile_page">
            <div className="container">
                {/* Header Section */}
                <div className="profile_section">
                    <div className="section_header">
                        <div className="section_header_row">
                            <div>
                                <h3>My Contacts</h3>
                                <p>{pageCount} contact{pageCount !== 1 ? 's' : ''} in your network</p>
                            </div>
                            <div className="contacts_header_actions">
                                <div className="search_input_wrapper">
                                    <SearchIcon className="search_icon" />
                                    <input
                                        type="search"
                                        placeholder="Search contacts..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="contacts_search_input"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="add_contact_btn"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addContacts"
                                    onClick={() => setModalType("add")}
                                >
                                    <PersonAddIcon fontSize="small" />
                                    Add Contact
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="section_content">
                        {loading ? (
                            <div className="loading_state">
                                <ThreeDots height="60" width="80" radius="9" color="#B1903F" visible={true} />
                                <p>Loading contacts...</p>
                            </div>
                        ) : contact.length === 0 ? (
                            <div className="empty_state">
                                <PeopleIcon className="empty_icon" />
                                <h4>No Contacts Yet</h4>
                                <p>Add contacts to start building your network and send project invitations.</p>
                                <button
                                    type="button"
                                    className="empty_state_btn"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addContacts"
                                    onClick={() => setModalType("add")}
                                >
                                    <PersonAddIcon fontSize="small" />
                                    Add Your First Contact
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Select All for Invitations */}
                                {jobId && (
                                    <div className="select_all_row">
                                        <label className="select_all_label">
                                            <input
                                                type="checkbox"
                                                onChange={handleCheckboxAllChange}
                                                checked={selectedContacts.length === contact.length && contact.length > 0}
                                            />
                                            <span>Select all ({selectedContacts.length} selected)</span>
                                        </label>
                                    </div>
                                )}

                                {/* Contacts Grid */}
                                <div className="contacts_grid">
                                    {contact.map((contactItem, index) => (
                                        <div key={index} className={`contact_card ${selectedContacts.includes(contactItem.id) ? 'selected' : ''}`}>
                                            {jobId && (
                                                <div className="contact_checkbox">
                                                    <input
                                                        type="checkbox"
                                                        onChange={(e) => handleCheckboxChange(e, contactItem.id)}
                                                        checked={selectedContacts.includes(contactItem.id)}
                                                    />
                                                </div>
                                            )}
                                            <div className="contact_card_header">
                                                <div className="contact_avatar">
                                                    {contactItem?.name?.charAt(0)?.toUpperCase() || "?"}
                                                </div>
                                                <div className="contact_dropdown">
                                                    <button
                                                        type="button"
                                                        className="dropdown_trigger"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                    >
                                                        <MoreVertIcon />
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                type="button"
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#addContacts"
                                                                onClick={() => handleUpdate(contactItem)}
                                                            >
                                                                <EditIcon fontSize="small" /> Edit
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                className="dropdown-item delete_item"
                                                                type="button"
                                                                onClick={() => handleDelete(contactItem)}
                                                            >
                                                                <DeleteIcon fontSize="small" /> Delete
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="contact_card_body">
                                                <h4 className="contact_name">{contactItem?.name || "Unknown"}</h4>
                                                {contactItem?.business_name && (
                                                    <p className="contact_business">
                                                        <BusinessIcon fontSize="small" />
                                                        {contactItem.business_name}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="contact_card_footer">
                                                {contactItem?.phone_number && (
                                                    <a href={`tel:${contactItem.phone_number}`} className="contact_link">
                                                        <PhoneIcon fontSize="small" />
                                                        {contactItem.phone_number}
                                                    </a>
                                                )}
                                                {contactItem?.email && (
                                                    <a href={`mailto:${contactItem.email}`} className="contact_link">
                                                        <EmailIcon fontSize="small" />
                                                        {contactItem.email}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {pageCount > 10 && (
                                    <div className="pagination_wrapper">
                                        <ReactPaginate
                                            previousLabel={"Prev"}
                                            nextLabel={"Next"}
                                            breakLabel={"..."}
                                            pageCount={count}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageClick}
                                            containerClassName={"pagination"}
                                            pageClassName={"page-item"}
                                            pageLinkClassName={"item-link"}
                                            previousClassName={"page-item"}
                                            previousLinkClassName={"item-link"}
                                            nextClassName={"page-item"}
                                            nextLinkClassName={"item-link"}
                                            breakClassName={"page-item"}
                                            breakLinkClassName={"item-link"}
                                            activeClassName={"active"}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Action Button */}
                <div className="profile_actions centered">
                    {jobId ? (
                        <button
                            type="button"
                            className="save_btn"
                            onClick={handleInvitation}
                            disabled={selectedContacts.length === 0}
                        >
                            <SendIcon fontSize="small" />
                            Send Invitation ({selectedContacts.length})
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="save_btn outline"
                            onClick={() => handleButtonClick(5)}
                        >
                            <CloudUploadIcon fontSize="small" />
                            Upload Contacts
                        </button>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            {contactToDelete && (
                <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
                    <div className="delete_dialog">
                        <DialogTitle className="delete_dialog_title">
                            Delete Contact
                        </DialogTitle>
                        <div className="delete_dialog_content">
                            <p>Are you sure you want to delete <strong>{contactToDelete.name}</strong>?</p>
                            <p className="delete_warning">This action cannot be undone.</p>
                        </div>
                        <div className="delete_dialog_actions">
                            <button onClick={handleCloseDeleteModal} className="cancel_btn">
                                Cancel
                            </button>
                            <button onClick={handleConfirmDelete} className="delete_btn">
                                Delete
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}

            {/* Add/Edit Contact Modal */}
            <AddContacts
                contact={contact}
                onDataReceived={receiveDataFromAddContacts}
                modalType={modalType}
                dataToUpdate={dataToUpdate}
                updateContact={updateContact}
            />
        </div>
    );
}
